import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PlotlyModule } from 'angular-plotly.js';
import { ProductionBatch, Scan, Lab } from '../../../model';
import { AxisSpans } from './batch-analysis.component';
import { ModelState } from '../../../store/model/model.state';

const LOG = '[Predicted3D]';

/**
 * Normalised threshold offsets (relative to target color).
 * Kept separate from InspectionModel so callers don't have to know
 * which raw model fields map to which axis.
 */
interface Thresholds {
  lMin: number; lMax: number;
  aMin: number; aMax: number;
  bMin: number; bMax: number;
}

/**
 * 3-D scatter plot of **predicted** LAB values for every scan in a batch.
 *
 * Why predicted vs measured?  The model output (predicted) is what the
 * pass/fail decision is based on, so operators need to see where it lands
 * relative to the tolerance zone — not just where the raw camera reading was.
 *
 * Overlaid traces (all optional, driven by the inspection model):
 *  - Blue X  — target color anchor so operators immediately see the aim point
 *  - Blue sphere — ΔE tolerance shell; a scan is acceptable anywhere inside it
 *  - Green box   — per-axis (dL/da/db) tolerance parallelepiped; used when
 *                  the model is NOT in ΔE mode, or when useThresholds is forced on
 */
@Component({
  selector: 'app-batch-predicted-3d-plot',
  imports: [CommonModule, PlotlyModule],
  template: `
    <div class="plot-container">
      <plotly-plot
        [data]="plotData()"
        [layout]="plotLayout()"
        [config]="plotConfig()"
        (plotlyClick)="onPointClick($event)"
        [style]="{ width: '100%', height: '100%' }"
      ></plotly-plot>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        isolation: isolate;
        border: 1px solid #d9d9d9;
      }
      .plot-container {
        width: 100%;
        height: 600px;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchPredicted3dPlotComponent {
  batch = input.required<ProductionBatch>();

  /**
   * When provided by the parent (e.g. a sibling measured-LAB plot),
   * both charts share the same axis scale so visual comparison is valid.
   * When null the component auto-calculates spans from its own data.
   */
  axisSpans = input<AxisSpans | null>(null);

  /**
   * Show the ΔE sphere even when the model's primary mode is per-axis thresholds.
   * Why: some inspectors want to see the ΔE context regardless of pass/fail mode.
   */
  useDeltaE = input<boolean>(true);

  /**
   * Show the per-axis threshold box even when the model is in ΔE mode.
   * Why: helps operators understand both tolerance shapes simultaneously.
   */
  useThresholds = input<boolean>(true);

  scanSelected = output<Scan>();

  // ModelState is injected rather than received as an input because the
  // inspection model is a global concern — passing it down through every
  // parent would create unnecessary coupling.
  private modelState = inject(ModelState);

  /** LAB aim point from the batch's catalog color entry. Null if color data is missing. */
  targetColor = computed<Lab | null>(() => this.batch().color?.lab ?? null);

  /**
   * The inspection model used for the scans in this batch.
   * We take the first scan that carries a model ID because all scans in a
   * batch are evaluated against the same model — we just need any one of them.
   */
  inspectionModelId = computed<string | null>(() => {
    const scans = this.batch().scans ?? [];
    return scans.find(s => s.inspectionModelId != null)?.inspectionModelId ?? null;
  });

  /** Resolved inspection model, or null if scans carry no model reference. */
  inspectionModel = computed(() => {
    const id = this.inspectionModelId();
    if (!id) {
      console.debug(`${LOG} No inspectionModelId on any scan — tolerance overlays will be hidden`);
      return null;
    }
    const m = this.modelState.all.find(m => m.id === id) ?? null;
    if (!m) console.warn(`${LOG} inspectionModelId ${id} not found in ModelState.all`);
    return m;
  });

  /**
   * Auto-calculated spans that guarantee every relevant point is visible.
   * Falls back to the parent-supplied axisSpans when available — that keeps
   * this plot axis-aligned with the measured-LAB chart for easy comparison.
   *
   * When computing autonomously we include the target, all scan predictions,
   * the deltaE sphere extents and the threshold box extents, then add 1 LAB
   * unit of padding on each side so the outermost geometry never hugs the edge.
   */
  effectiveAxisSpans = computed<AxisSpans>(() => {
    if (this.axisSpans()) {
      console.debug(`${LOG} Using parent-supplied axisSpans`, this.axisSpans());
      return this.axisSpans()!;
    }

    const lVals: number[] = [], aVals: number[] = [], bVals: number[] = [];
    const push = (l: number, a: number, b: number) => { lVals.push(l); aVals.push(a); bVals.push(b); };

    const target = this.targetColor();
    if (target) push(target.l, target.a, target.b);

    for (const s of this.batch().scans ?? []) {
      if (s.predicted) push(s.predicted.l, s.predicted.a, s.predicted.b);
    }

    // Sphere extends equally on all axes — push the six axis-aligned extremes
    const de = this.deltaE();
    if (target && de != null) {
      push(target.l + de, target.a + de, target.b + de);
      push(target.l - de, target.a - de, target.b - de);
    }

    // Threshold box corners differ per axis, so push min/max independently
    const thr = this.thresholds();
    if (target && thr) {
      lVals.push(target.l + thr.lMin, target.l + thr.lMax);
      aVals.push(target.a + thr.aMin, target.a + thr.aMax);
      bVals.push(target.b + thr.bMin, target.b + thr.bMax);
    }

    // +2 = 1 unit pad on each side so the outermost geometry isn't clipped
    const span = (vals: number[]) => vals.length ? Math.max(...vals) - Math.min(...vals) + 2 : 10;
    const spans = { l: span(lVals), a: span(aVals), b: span(bVals) };
    console.debug(`${LOG} Auto-computed axisSpans`, spans);
    return spans;
  });

  /**
   * The ΔE threshold radius (in LAB units) to draw as a sphere around the target.
   *
   * Shown when:
   *  - The model's primary mode IS ΔE  (isUsingDeltaE === true), OR
   *  - useDeltaE input is explicitly true (caller wants to see it regardless)
   */
  deltaE = computed<number | null>(() => {
    const m = this.inspectionModel();
    if (!m) return null;
    if (!m.isUsingDeltaE && !this.useDeltaE()) return null;
    const de = m.deThreshold ?? null;
    console.debug(`${LOG} deltaE threshold`, de, '(model isUsingDeltaE:', m.isUsingDeltaE, ')');
    return de;
  });

  /**
   * Per-axis tolerance offsets from the target color.
   *
   * Shown when:
   *  - The model's primary mode is NOT ΔE (isUsingDeltaE === false), OR
   *  - useThresholds input is explicitly true
   *
   * Returns null when any of the six threshold values is missing on the model,
   * because a partial box would be misleading.
   */
  thresholds = computed<Thresholds | null>(() => {
    const m = this.inspectionModel();
    if (!m) return null;
    if (m.isUsingDeltaE && !this.useThresholds()) return null;

    const { dlMinThreshold, dlMaxThreshold, daMinThreshold, daMaxThreshold, dbMinThreshold, dbMaxThreshold } = m;

    // All six values must be present — a partial box would give a wrong visual impression
    if (dlMinThreshold == null || dlMaxThreshold == null ||
      daMinThreshold == null || daMaxThreshold == null ||
      dbMinThreshold == null || dbMaxThreshold == null) {
      console.warn(`${LOG} Incomplete threshold values on model ${m.id} — threshold box hidden`);
      return null;
    }

    const thr: Thresholds = {
      lMin: dlMinThreshold, lMax: dlMaxThreshold,
      aMin: daMinThreshold, aMax: daMaxThreshold,
      bMin: dbMinThreshold, bMax: dbMaxThreshold,
    };
    console.debug(`${LOG} Thresholds`, thr);
    return thr;
  });

  /**
   * All Plotly traces for the plot.
   *
   * Trace order matters for z-ordering in Plotly — scatter points last so
   * they render on top of the semi-transparent geometry.
   */
  plotData = computed(() => {
    const b = this.batch();
    if (!b?.scans) {
      console.warn(`${LOG} plotData: batch has no scans`);
      return [];
    }

    // Only scans that carry a model prediction — raw-only scans have no LAB prediction
    const scans = b.scans.filter((s) => !!s.predicted);
    console.debug(`${LOG} plotData: ${scans.length} / ${b.scans.length} scans have predictions`);

    const x = scans.map((s) => s.predicted!.l);
    const y = scans.map((s) => s.predicted!.a);
    const z = scans.map((s) => s.predicted!.b);

    const text = scans.map((s) => {
      const lab = s.predicted!;
      return `Scan: ${s.sequenceInBatch}<br>Status: ${s.state}<br>L*: ${lab.l.toFixed(2)}<br>a*: ${lab.a.toFixed(2)}<br>b*: ${lab.b.toFixed(2)}`;
    });

    // Color encodes pass/fail status — the most important visual at a glance
    const getColor = (s: Scan) => {
      switch (s.state) {
        case 'Passed': return '#28a745';
        case 'Failed': return '#dc3545';
        case 'Ignored': return '#6c757d';
        case 'Scanning': return '#007bff';
        default: return '#ffc107'; // unknown / pending
      }
    };

    const traces: object[] = [];

    const target = this.targetColor();

    // ── Tolerance geometry (behind scatter points) ──────────────────────────

    // ΔE sphere: represents the acceptable color difference radius in LAB space
    const de = this.deltaE();
    if (target && de != null && de > 0) {
      console.debug(`${LOG} Adding deltaE sphere, r=${de}`);
      traces.push(buildSphereMesh(target, de, 'rgba(100,160,255,0.5)'));
    }

    // Per-axis threshold box: represents the asymmetric dL/da/db tolerance zone
    const thr = this.thresholds();
    if (target && thr) {
      console.debug(`${LOG} Adding threshold parallelepiped`, thr);
      traces.push(buildParallelpiped(target, thr, 'rgba(144,238,144,0.5)'));
    }

    // ── Scatter points (in front of geometry) ───────────────────────────────

    traces.push({
      x, y, z,
      mode: 'markers',
      type: 'scatter3d',
      text,
      hoverinfo: 'text',
      // customdata carries scan IDs so onPointClick can identify which scan was clicked
      customdata: scans.map((s) => s.id),
      marker: {
        size: 5,
        color: scans.map((s) => getColor(s)),
        opacity: 0.8,
        line: { color: 'white', width: 0.5 },
      },
    });

    // Target color marker: always drawn last so it sits on top of everything
    if (target) {
      traces.push({
        x: [target.l], y: [target.a], z: [target.b],
        mode: 'markers',
        type: 'scatter3d',
        hoverinfo: 'text',
        text: [`Target L*: ${target.l.toFixed(2)}<br>a*: ${target.a.toFixed(2)}<br>b*: ${target.b.toFixed(2)}`],
        marker: {
          symbol: 'x',
          size: 6,
          color: '#0000ff',
          opacity: 1.0,
        },
      });
    }

    return traces;
  });

  /**
   * Computes axis ranges from all trace data so every point and geometry
   * vertex is visible without manual zoom.
   *
   * We re-derive ranges from the raw trace arrays rather than from the
   * computed signals because Plotly needs the final rendered extents —
   * the sphere/box vertices are already included in those arrays.
   *
   * aspectmode:'data' keeps LAB units equal on all three axes, which is
   * critical — a squashed sphere would look like an ellipsoid and mislead
   * operators about the actual tolerance shape.
   */
  plotLayout = computed(() => {
    const data = this.plotData();
    const spans = this.effectiveAxisSpans();
    const allX: number[] = [];
    const allY: number[] = [];
    const allZ: number[] = [];

    // Flatten all trace vertex arrays to find the global extents
    for (const trace of data as { x?: number[]; y?: number[]; z?: number[] }[]) {
      if (Array.isArray(trace.x)) allX.push(...trace.x);
      if (Array.isArray(trace.y)) allY.push(...trace.y);
      if (Array.isArray(trace.z)) allZ.push(...trace.z);
    }

    /**
     * Centers the axis range on the data midpoint and applies the supplied span.
     * Using a fixed span (from effectiveAxisSpans) rather than auto-fit prevents
     * the camera from jumping when new scans arrive mid-batch.
     */
    const centeredRange = (values: number[], sharedSpan: number | undefined) => {
      if (!values.length) return undefined;
      const min = Math.min(...values);
      const max = Math.max(...values);
      const center = (min + max) / 2;
      const span = sharedSpan ?? (max - min || 1);
      const half = span / 2;
      const pad = span * 0.1; // 10 % breathing room so markers aren't flush with the axis wall
      return [center - half - pad, center + half + pad];
    };

    const rangeX = centeredRange(allX, spans?.l);
    const rangeY = centeredRange(allY, spans?.a);
    const rangeZ = centeredRange(allZ, spans?.b);

    console.debug(`${LOG} plotLayout ranges — L*:`, rangeX, 'a*:', rangeY, 'b*:', rangeZ);

    return {
      margin: { l: 0, r: 0, b: 0, t: 0 },
      scene: {
        xaxis: { title: { text: 'L*' }, range: rangeX },
        yaxis: { title: { text: 'a*' }, range: rangeY },
        zaxis: { title: { text: 'b*' }, range: rangeZ },
        aspectmode: 'data',
      },
      showlegend: false,
    };
  });

  plotConfig = computed(() => ({
    responsive: true,
    displayModeBar: true,
    scrollZoom: true,
  }));

  /**
   * Translates a Plotly click event into a typed Scan emission.
   * customdata on each marker point holds the scan ID, set at trace-build time.
   */
  onPointClick(event: unknown) {
    const point = (event as { points?: { customdata?: string }[] }).points?.[0];
    if (!point?.customdata) return;

    const scan = this.batch().scans.find((s) => s.id === point.customdata);
    if (scan) {
      console.debug(`${LOG} Scan clicked: id=${scan.id} seq=${scan.sequenceInBatch} state=${scan.state}`);
      this.scanSelected.emit(scan);
    }
  }
}

/**
 * Builds a Plotly mesh3d sphere centered on `center` with radius `r`.
 *
 * Why mesh3d instead of a surface trace?  mesh3d uses explicit vertex/face
 * arrays which integrate cleanly with other mesh3d traces (the threshold box)
 * and avoids the axis-distortion that surface traces can introduce.
 *
 * The UV parameterisation (phi = polar, theta = azimuthal) produces a standard
 * latitude/longitude sphere.  12 steps is enough for smooth appearance at the
 * semi-transparent opacities used here without generating excessive geometry.
 */
function buildSphereMesh(center: Lab, r: number, color: string): object {
  const steps = 12;
  const xs: number[] = [], ys: number[] = [], zs: number[] = [];
  const is: number[] = [], js: number[] = [], ks: number[] = [];

  // Generate (steps+1)² vertices on the sphere surface
  for (let pi = 0; pi <= steps; pi++) {
    const phi = (Math.PI * pi) / steps; // 0 → π  (north to south pole)
    for (let ti = 0; ti <= steps; ti++) {
      const theta = (2 * Math.PI * ti) / steps; // 0 → 2π (full longitude sweep)
      // LAB axes: L* = x, a* = y, b* = z
      xs.push(center.l + r * Math.sin(phi) * Math.cos(theta));
      ys.push(center.a + r * Math.sin(phi) * Math.sin(theta));
      zs.push(center.b + r * Math.cos(phi));
    }
  }

  // Triangulate the UV grid — each quad becomes two triangles (i,j,k) + (b,d,c)
  const w = steps + 1; // vertex row width
  for (let pi = 0; pi < steps; pi++) {
    for (let ti = 0; ti < steps; ti++) {
      const a = pi * w + ti;
      const b = pi * w + ti + 1;
      const c = (pi + 1) * w + ti;
      const d = (pi + 1) * w + ti + 1;
      is.push(a, b); js.push(b, d); ks.push(c, c);
    }
  }

  return {
    type: 'mesh3d', x: xs, y: ys, z: zs,
    i: is, j: js, k: ks,
    opacity: 0.15, color,
    flatshading: false, // smooth shading makes the sphere visually distinguishable from the flat box
    hoverinfo: 'none',
  };
}

/**
 * Builds a Plotly mesh3d axis-aligned box (parallelepiped) representing
 * the per-axis (dL/da/db) tolerance zone around the target color.
 *
 * The 8-vertex / 12-triangle encoding is the standard Plotly mesh3d cuboid
 * layout.  Vertex order: bottom face (z=bMin) first, top face (z=bMax) second,
 * winding chosen so normals point outward for correct back-face culling.
 *
 * Thresholds are already absolute LAB coordinates (caller adds target offset).
 */
function buildParallelpiped(center: Lab, thr: Thresholds, color: string): object {
  // Convert per-axis offsets to absolute LAB coordinates
  const lMin = center.l + thr.lMin, lMax = center.l + thr.lMax;
  const aMin = center.a + thr.aMin, aMax = center.a + thr.aMax;
  const bMin = center.b + thr.bMin, bMax = center.b + thr.bMax;

  return {
    type: 'mesh3d',
    // 8 corners of the box:  0-3 = bottom (bMin), 4-7 = top (bMax)
    x: [lMin, lMin, lMax, lMax, lMin, lMin, lMax, lMax],
    y: [aMin, aMax, aMax, aMin, aMin, aMax, aMax, aMin],
    z: [bMin, bMin, bMin, bMin, bMax, bMax, bMax, bMax],
    // 12 triangles forming 6 faces (standard cuboid i/j/k triplets)
    i: [7, 0, 0, 0, 4, 4, 6, 6, 4, 0, 3, 2],
    j: [3, 4, 1, 2, 5, 6, 5, 2, 0, 1, 6, 3],
    k: [0, 7, 2, 3, 6, 7, 1, 1, 5, 5, 7, 6],
    opacity: 0.2, color,
    flatshading: true, // flat shading on a box makes each face clearly distinct
    hoverinfo: 'none',
  };
}
